import type { DemoState } from '../state';
import { onLocaleChange, t } from '../i18n';
import { openShortcutsPopup } from './shortcuts-popup';
import { isGpuLikeBackend } from '@amitone/dither-core';

export interface StatusInfo {
  loaded: boolean;
  outW: number;
  outH: number;
  processedW: number;
  processedH: number;
  elapsedMs: number;
  processing: boolean;
  /** Optional algorithm name surfaced as `METHOD · {name}` on the right of
   * the status text - uppercased per the spec. */
  methodLabel?: string | null;
  /** Optional i18n key for inline warnings, such as labelmap import errors. */
  messageKey?: string | null;
}

export type StatusGpuMode = 'idle' | 'cpu' | 'gpu';

export function statusGpuModeFromState(
  state: Pick<DemoState, 'source' | 'lastGpuDiagnostics'>,
): StatusGpuMode {
  if (!state.source) return 'idle';
  const backend = state.lastGpuDiagnostics?.backend;
  return backend && isGpuLikeBackend(backend) ? 'gpu' : 'cpu';
}

// Status bar redesigned per spec §6: 32px tall, monospace, dot + "/"
// dividers. State machine:
//
//   not loaded → muted dot + NO IMAGE LOADED
//   processing → violet pulsing dot + PROCESSING…
//   ready      → yellow dot + READY · {w} × {h}
//
// Right side shows version, active CPU/GPU mode, and the shortcuts link.
export function createStatusBar(parent: HTMLElement): {
  update(info: StatusInfo): void;
  updateGpuMode(mode: StatusGpuMode): void;
} {
  const bar = document.createElement('div');
  bar.className = 'statusbar';
  bar.setAttribute('role', 'status');
  parent.appendChild(bar);

  const dot = document.createElement('span');
  dot.className = 'dot idle';
  bar.appendChild(dot);

  const stateText = document.createElement('span');
  stateText.className = 'label';
  bar.appendChild(stateText);

  const pipe1 = document.createElement('span');
  pipe1.className = 'pipe';
  pipe1.textContent = '/';
  bar.appendChild(pipe1);

  const methodText = document.createElement('span');
  methodText.className = 'label';
  bar.appendChild(methodText);

  const pipe2 = document.createElement('span');
  pipe2.className = 'pipe';
  pipe2.textContent = '/';
  bar.appendChild(pipe2);

  const elapsedText = document.createElement('span');
  bar.appendChild(elapsedText);

  // Right-aligned footer: version, active compute mode, and shortcuts link.
  const right = document.createElement('span');
  right.className = 'right';
  let gpuMode: StatusGpuMode = 'idle';

  function renderRight(): void {
    right.replaceChildren();

    const version = document.createElement('span');
    // __APP_VERSION__ is injected by vite.config.ts define; falls back to '?' in test env.
    version.textContent = `v${typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '?'}`;

    const sep = document.createElement('span');
    sep.className = 'pipe';
    sep.textContent = '/';

    const mode = document.createElement('span');
    mode.className = 'label';
    mode.dataset['statusGpuMode'] = 'true';
    mode.textContent = gpuMode.toUpperCase();

    const sep2 = document.createElement('span');
    sep2.className = 'pipe';
    sep2.textContent = '/';

    // Shortcuts link - opens the shortcuts popup (Item 10c).
    const shortcutsBtn = document.createElement('button');
    shortcutsBtn.type = 'button';
    shortcutsBtn.className = 'statusbar-shortcuts-btn';
    shortcutsBtn.textContent = t('shortcuts.title');
    shortcutsBtn.addEventListener('click', () => {
      openShortcutsPopup(shortcutsBtn);
    });

    right.append(version, sep, mode, sep2, shortcutsBtn);
  }

  renderRight();
  bar.appendChild(right);

  let last: StatusInfo = {
    loaded: false,
    outW: 0,
    outH: 0,
    processedW: 0,
    processedH: 0,
    elapsedMs: 0,
    processing: false,
    methodLabel: null,
    messageKey: null,
  };

  function render(): void {
    if (!last.loaded) {
      dot.className = 'dot idle';
      stateText.textContent = t('status.no_image');
      methodText.textContent = '';
      elapsedText.textContent = '- ms';
      return;
    }

    if (last.processing) {
      dot.className = 'dot processing';
      stateText.textContent = t('status.processing');
    } else {
      dot.className = 'dot';
      stateText.textContent = t('status.ready', { w: last.outW, h: last.outH });
    }

    methodText.textContent = last.methodLabel
      ? t('status.method', { algorithm: last.methodLabel.toUpperCase() })
      : '';
    elapsedText.textContent = last.messageKey
      ? t(last.messageKey)
      : `${last.elapsedMs.toFixed(1)} ms`;
  }

  onLocaleChange(() => {
    render();
    renderRight();
  });
  render();

  return {
    update(info: StatusInfo): void {
      last = info;
      render();
    },
    updateGpuMode(mode: StatusGpuMode): void {
      gpuMode = mode;
      renderRight();
    },
  };
}
